import { Link } from '@/components/base/Link';

/**
 * 第二層版型：麵包屑。
 *
 * 原系統對應：`layout/breadcrumb.jsp:5-27`
 *   `<div class="row wrapper border-bottom white-bg page-heading"><ol class="breadcrumb">`
 *   第一節固定是 `main` 首頁（帶 `fa-home` 圖示，`:8`），之後最多三層
 *   （`param.breadcrumb1..3`，`:9-25`）；最後一節加 `.active`，第二層起文字包 `<strong>`。
 *   `.page-heading { padding: 0px 10px 20px 10px }`（`css/style.css:3473-3476`）。
 *
 * 節點資料由頁面層提供 —— 本元件不知道任何具體 route。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface BreadcrumbNode {
  id: string;
  label: string;
  /** 沒有 href 時原系統 render 成 `javascript:void(0)`（breadcrumb.jsp:10）—— 雛形改為純文字。 */
  href?: string;
  /** Icon 名稱（原系統首頁節點是 `fa fa-home`）。 */
  icon?: string;
}

export interface BreadcrumbProps {
  uiId: string;
  items: readonly BreadcrumbNode[];
}

export function Breadcrumb({ uiId, items }: BreadcrumbProps) {
  const lastIndex = items.length - 1;
  return (
    <nav
      data-ui-id={uiId}
      className="border-border border-b bg-surface px-[10px] pt-0 pb-5"
      aria-label="breadcrumb"
    >
      <ol data-ui-id={`${uiId}-list`} className="flex flex-wrap items-center gap-1 text-sm">
        {items.map((item, index) => {
          const active = index === lastIndex;
          // 原系統第二層起文字用 <strong>（breadcrumb.jsp:16,22）
          const emphasised = index > 0;
          return (
            <li
              key={item.id}
              data-ui-id={`${uiId}-item-${item.id}`}
              className={active ? 'font-bold text-ink' : 'text-ink-muted'}
              {...(active ? { 'aria-current': 'page' as const } : {})}
            >
              {index > 0 ? (
                <span data-ui-id={`${uiId}-sep-${item.id}`} aria-hidden="true" className="px-1">
                  /
                </span>
              ) : null}
              {item.href !== undefined && !active ? (
                <Link uiId={`${uiId}-link-${item.id}`} href={item.href}>
                  {emphasised ? <strong>{item.label}</strong> : item.label}
                </Link>
              ) : (
                <span data-ui-id={`${uiId}-text-${item.id}`}>
                  {emphasised ? <strong>{item.label}</strong> : item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
